import { runtimeConfig } from 'config/runtimeConfig';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';

const MID_WAY_URL = runtimeConfig.CONNECTION_API;

/**
 * Feed-processing modes for a channel + feed-type combo.
 *  - immediate : process feeds from the central queue as soon as they arrive
 *  - scheduled : dispatch from the queue on the configured cadence (see ScheduleForm)
 *  - manual    : "Off" / Processing Paused - no auto pickup, feeds held for manual processing
 * Exactly one mode is active at a time (mutually exclusive booleans on the API).
 */
export type ImportMode = 'immediate' | 'scheduled' | 'manual';

export interface ImportMethod {
    id?: string;
    channel_key: string;
    feed_type: string;
    immediate: boolean;
    scheduled: boolean;
    manual: boolean;
    enqueued_jobs: number;
    /** ISO timestamp of the last time the connector actually ran. Only meaningful
     *  for the Immediate mode, where the UI surfaces it under the option. */
    last_connector_trigger?: string;
}

export interface ImportMethodResult {
    success: boolean;
    data?: ImportMethod;
    error?: string;
}

// The API models the mode as three mutually-exclusive booleans. Keep the
// FE <-> API translation in one place so the rest of the UI thinks in `ImportMode`.
const modeFlags = (mode: ImportMode): Pick<ImportMethod, 'immediate' | 'scheduled' | 'manual'> => ({
    immediate: mode === 'immediate',
    scheduled: mode === 'scheduled',
    manual: mode === 'manual',
});

/** Derive the active `ImportMode` from an API record's boolean flags. */
export const modeFromMethod = (method?: Partial<ImportMethod> | null): ImportMode | null => {
    if (!method) return null;
    if (method.immediate) return 'immediate';
    if (method.scheduled) return 'scheduled';
    if (method.manual) return 'manual';
    return null;
};

/**
 * GET connection/import_method/{channelKey}/{feedType}
 * Fetches the current processing method for a channel + feed type.
 */
export const fetchImportMethod = async (
    channelKey: string,
    feedType: string,
): Promise<ImportMethodResult> => {
    try {
        const response = await ServiceWrapper.doGet(
            `${MID_WAY_URL}connection/import_method/${channelKey}/${feedType}`,
            null,
            null,
            AuthManager.getGmsToken(),
        );

        if (response?.errorfound || !response?.data?.done) {
            return { success: false, error: response?.data?.message ?? 'Failed to fetch import method' };
        }

        return { success: true, data: response.data.data as ImportMethod };
    } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
};

/**
 * POST connection/upsert_import_method
 * Persists the selected mode for a channel + feed type. The endpoint upserts:
 * when an existing record `id` is present in the payload it updates that record,
 * otherwise it creates a new one.
 */
export const persistImportMethod = async (
    channelKey: string,
    feedType: string,
    mode: ImportMode,
    id?: string,
): Promise<ImportMethodResult> => {
    try {
        const payload = {
            channel_key: channelKey,
            feed_type: feedType,
            ...modeFlags(mode),
            ...(id ? { id } : {}),
        };

        const response = await ServiceWrapper.doPost(
            `${MID_WAY_URL}connection/upsert_import_method`,
            payload,
            null,
            AuthManager.getGmsToken(),
        );

        if (response?.errorfound || !response?.data?.done) {
            return { success: false, error: response?.data?.message ?? 'Failed to save import method' };
        }

        return { success: true, data: response.data.data as ImportMethod };
    } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
};
